import { useState, type ReactNode } from 'react';
import { Skeleton } from '@monksflow/monks-ui';
import { AboutPageDialog, type AboutPageDialogProps } from '@/shared/ui';
import {
  ListPageBody,
  ListPageLayout,
  PageHeader,
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderEditableName,
  PageHeaderIdentity,
  PageHeaderTitle,
} from '@/shared/ui/layout';
import { SettingsRailToggle } from '../OrganizationSettingsLayout';

interface AdminPageShellProps {
  /** Names the page in the header. */
  title: string;
  /**
   * The pages above this one, root first. A page reached from a list gives at
   * least the list it came from, so the breadcrumb can offer the way back.
   */
  parents?: { label: string; href: string }[];
  /**
   * Opt out of the body card's inset, for a page that already pays it the way
   * every other redesigned list page does: the toolbar and the table's scroll
   * region each carry their own, so paying it here too insets them twice.
   */
  ownsBodyInset?: boolean;
  /**
   * No body card at all, for a page that lays its own cards out on the page with the page's gap
   * between them, as the chat page does.
   */
  cardless?: boolean;
  /** Makes the title renamable in place; `label` names the field for assistive tech. */
  rename?: { onCommit: (next: string) => void; label: string };
  /** The title is still loading: its place in the breadcrumb is held, so the header does not jump. */
  titlePending?: boolean;
  /** Drawn before the title in the breadcrumb, such as the tool badge on a tool's page. */
  titleIcon?: ReactNode;
  /** Drawn at the right of the header, such as a page's own toolbar. */
  actions?: ReactNode;
  /** The page's "About" dialog, opened by an info button after a top-level title. */
  about?: Omit<AboutPageDialogProps, 'open' | 'onOpenChange'>;
  children: ReactNode;
}

/**
 * The surface an `/admin` settings page draws itself on. The frame around the
 * page supplies nothing but an inset, so the page owns the header card and the
 * body card every other list page owns.
 */
export function AdminPageShell({
  title,
  parents = [],
  ownsBodyInset = false,
  cardless = false,
  rename,
  titlePending = false,
  titleIcon,
  actions,
  about,
  children,
}: AdminPageShellProps) {
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const currentCrumb = titlePending ? (
    <Skeleton data-testid="page-header-title-pending" className="h-5 w-40" />
  ) : rename ? (
    <PageHeaderEditableName value={title} onCommit={rename.onCommit} label={rename.label} />
  ) : (
    <PageHeaderBreadcrumbPage>{title}</PageHeaderBreadcrumbPage>
  );

  return (
    <ListPageLayout>
      {parents.length > 0 ? (
        <PageHeader focus>
          <PageHeaderIdentity>
            <SettingsRailToggle />
            <PageHeaderBreadcrumb>
              {parents.map((parent) => (
                <PageHeaderBreadcrumbLink key={parent.href} href={parent.href}>
                  {parent.label}
                </PageHeaderBreadcrumbLink>
              ))}
              {titleIcon ? (
                <span className="flex min-w-0 items-center gap-xs">
                  {titleIcon}
                  {currentCrumb}
                </span>
              ) : (
                currentCrumb
              )}
            </PageHeaderBreadcrumb>
          </PageHeaderIdentity>
          {actions && <PageHeaderActions>{actions}</PageHeaderActions>}
        </PageHeader>
      ) : (
        <PageHeader focus>
          <PageHeaderIdentity>
            <SettingsRailToggle />
            <PageHeaderTitle {...(about ? { info: { onClick: () => setIsAboutOpen(true) } } : {})}>
              {title}
            </PageHeaderTitle>
          </PageHeaderIdentity>
          {actions && <PageHeaderActions>{actions}</PageHeaderActions>}
        </PageHeader>
      )}

      {cardless ? (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      ) : (
        <ListPageBody>
          {ownsBodyInset ? (
            children
          ) : (
            <div className="min-h-0 flex-1 overflow-auto p-section">{children}</div>
          )}
        </ListPageBody>
      )}

      {about && <AboutPageDialog {...about} open={isAboutOpen} onOpenChange={setIsAboutOpen} />}
    </ListPageLayout>
  );
}
